import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFlag } from 'flags';
import { Formik, Form, Field } from 'formik';
import { debounce } from 'lodash';

import * as yup from 'yup';

import Button from 'components/Button/Button';
import Filter from 'components/Filter/Filter';
import Tooltip from 'components/Tooltip/Tooltip';
import Pagination from 'components/Pagination/Pagination';
import DirectionIcon from 'components/DirectionIcon/DirectionIcon';
import Checkbox from 'components/Checkbox/Checkbox';
import { getTemplateThumb } from 'helpers/campaignHelper';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';
import placeholderListViewImg from 'static/images/placeholder-list-view.png';

import colors from 'theme/Colors.module.scss';
import './Campaigns.scss';

import {
  Box,
  Button as RegularButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormErrorMessage,
  Progress,
  Flex,
  Image,
  Switch,
  Grid,
  GridItem,
  useBreakpointValue,
} from '@chakra-ui/react';

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import {
  useGetCampaignsQuery,
  useUpdateCampaignByIdMutation,
} from 'modules/campaigns/campaignsApi';
import { useGetTemplatesQuery } from 'modules/templates/templatesApi';

const FORM_SCHEMA = yup.object().shape({
  priority: yup.number().integer().min(1),
});

const Campaigns = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isMobileView = useBreakpointValue({
    base: true,
    lg: false,
  });

  const isReadOnly = useFlag(['features', 'isReadOnly']);
  const [editingPriorityRowId, setEditingPriorityRowId] = useState(null);

  const [sorting, setSorting] = useState([
    {
      id: 'priority',
      direction: 'asc-nulls-last',
    },
  ]);

  // Raw search input (not debounced)
  const [searchInput, setSearchInput] = useState('');

  // Debounced search parameters
  const [defaultSearchParameters, setDefaultSearchParameters] = useState({
    search: '',
    sort: [
      {
        field: 'priority',
        direction: 'asc-nulls-last',
      },
    ],
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(query => {
        setDefaultSearchParameters(prev => ({ ...prev, search: query }));
        setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
      }, 500), // Adjust debounce delay
    [] // No dependencies, so it's created only once
  );

  // Wrap it in useCallback so it doesn't change on every render
  const handleDebouncedSearch = useCallback(
    query => {
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const {
    data: campaignsData,
    error,
    isLoading,
    isFetching,
    isSuccess,
  } = useGetCampaignsQuery({
    ...defaultSearchParameters,
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
    sort: defaultSearchParameters.sort || [],
  });

  const { data: templatesData } = useGetTemplatesQuery({
    ...defaultSearchParameters,
    // page: pagination.pageIndex + 1,
    // size: pagination.pageSize,
    size: 500,
    sort: defaultSearchParameters.sort || [],
  });

  const [updateCampaignById] = useUpdateCampaignByIdMutation();

  const [localCampaigns, setLocalCampaigns] = useState([]);

  useEffect(() => {
    if (isSuccess && campaignsData?.data.entities) {
      setLocalCampaigns(campaignsData?.data.entities);
    }
  }, [isSuccess, campaignsData]);

  const columns = [
    {
      accessorKey: '',
      header: '',
      id: 'enabled-control',
      enableSorting: false,
      width: '60px',
      cell: ({ row }) => {
        const campaignData = row.original;
        return (
          <Box
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <Switch
              size="md"
              colorScheme="green"
              disabled={isReadOnly}
              defaultChecked={campaignData.enabled}
              onChange={event => {
                const enabled = event.target.checked;
                debouncedUpdate(campaignData, enabled);
              }}
            />
          </Box>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Campaign Name',
      id: 'name',
      cell: props => {
        const campaignData = props.row.original;
        return (
          <Flex>
            {!isReadOnly && !isMobileDevice && (
              <Text as={'span'} className="cmp-header__drag-handle">
                <i className="fa fa-grip-vertical" />
              </Text>
            )}
            <Text as={'span'} className="cmp-header__legend">
              <i
                className="fas fa-circle"
                style={{
                  color:
                    campaignData.target_type === 'open'
                      ? colors.primary900
                      : colors.secondary900,
                }}
              />
            </Text>
            <Text>{props.getValue()}</Text>
            {campaignData.holdout_pct > 0 && (
              <Text
                as="span"
                fontSize="xs"
                bg="#FEF3CD"
                color="#856404"
                px={2}
                py={0.5}
                borderRadius="full"
                ml={2}
                whiteSpace="nowrap"
                fontWeight="semibold"
              >
                {campaignData.holdout_pct}% Holdout
              </Text>
            )}
          </Flex>
        );
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      id: 'priority',
      textAlign: 'center',
      width: '100px',
      cell: props => {
        const rowId = props.row.id; // or props.row.original.id if your id is inside data
        const isEditing = editingPriorityRowId === rowId;

        if (isReadOnly) return <Text>{props.getValue() || '--'}</Text>;

        if (!isEditing)
          return (
            <Text
              style={{
                cursor: 'text',
              }}
              onClick={e => {
                e.stopPropagation();
                setEditingPriorityRowId(rowId);
              }}
            >
              {props.getValue() || '--'}
              <RegularButton
                className="cmp-priority__edit-btn"
                size="sm"
                style={{
                  color: colors.slate,
                }}
                onClick={() => {
                  setEditingPriorityRowId(null);
                }}
              >
                <i className="fas fa-edit" />
              </RegularButton>
            </Text>
          );

        return (
          <Formik
            validateOnMount
            initialValues={{
              priority: props.getValue()?.toString() || '',
              campaignId: props.row.original.id,
            }}
            validationSchema={FORM_SCHEMA}
            onSubmit={handleUpdateCampaignPriority}
          >
            {form => {
              const { isSubmitting, isValid, dirty } = form;
              const submitButtonDisabled = isSubmitting || !isValid || !dirty;

              return (
                <Form className="cmp-priority__form">
                  <Flex>
                    <Field name="priority">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.priority && form.touched.priority
                          }
                        >
                          <InputGroup>
                            <Input
                              {...field}
                              className={`cmp-priority__input`}
                              placeholder="--"
                              focusBorderColor={colors.primary600}
                              isDisabled={isSubmitting}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            />
                            <InputRightElement>
                              {dirty ? (
                                <RegularButton
                                  className="cmp-priority__btn"
                                  size="sm"
                                  type="submit"
                                  style={{
                                    color: submitButtonDisabled
                                      ? colors.slate
                                      : colors.primary900,
                                  }}
                                  isDisabled={submitButtonDisabled}
                                >
                                  <i className={`fas fa-save`} />
                                </RegularButton>
                              ) : (
                                <RegularButton
                                  className="cmp-priority__btn"
                                  size="sm"
                                  style={{
                                    color: colors.slate,
                                  }}
                                  onClick={() => {
                                    setEditingPriorityRowId(null);
                                  }}
                                >
                                  <i className="fas fa-times-circle" />
                                </RegularButton>
                              )}
                            </InputRightElement>
                          </InputGroup>
                          <FormErrorMessage className="cmp-priority__error-message">
                            {form.errors.priority}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Flex>
                </Form>
              );
            }}
          </Formik>
        );
      },
    },
    {
      accessorKey: '',
      header: 'Status',
      id: 'status',
      textAlign: 'center',
      cell: ({ row }) => {
        const campaignData = row.original;
        const status = getStatus(campaignData);
        return <Text>{status || '--'}</Text>;
      },
    },
    {
      accessorKey: '',
      header: 'Personalized Prints',
      id: 'delivery_limit',
      textAlign: 'center',
      cell: ({ row }) => {
        const campaignData = row.original;
        const delivered = (campaignData.fulfill_count || 0).toLocaleString();
        const personalized_prints =
          campaignData.delivery_limit === null
            ? `${delivered} / ∞`
            : `${delivered} / ${campaignData.delivery_limit.toLocaleString()}`;
        return <Text>{personalized_prints || '--'}</Text>;
      },
    },
    {
      accessorKey: 'start_date',
      header: 'Start Date',
      id: 'start_date',
      textAlign: 'center',
      cell: props => (
        <Text>
          {props.getValue()
            ? formatUTCDateTime(new Date(props.getValue()))
            : '--'}
        </Text>
      ),
    },
    {
      accessorKey: 'end_date',
      header: 'Ends',
      id: 'end_date',
      textAlign: 'center',
      cell: props => (
        <Text>
          {props.getValue()
            ? formatUTCDateTime(new Date(props.getValue()))
            : '--'}
        </Text>
      ),
    },
    {
      accessorKey: '',
      header: 'Template',
      id: 'template',
      textAlign: 'center',
      enableSorting: false,
      cell: ({ row }) => {
        const campaignData = row.original;
        const template = templatesData?.data.entities.find(
          template => template.id === campaignData.ad_template_id
        );
        const hasTemplateThumb = template && getTemplateThumb(template);
        const renderTemplateTooltip = () => {
          return (
            <Box className="cmp-table__template-tooltip">
              <Image src={getTemplateThumb(template)} alt="" />
              <Text>{template.name}</Text>
            </Box>
          );
        };
        return (
          template &&
          (hasTemplateThumb ? (
            <Tooltip tipText={renderTemplateTooltip()} direction="left">
              <Image
                className="cmp-table__template-preview"
                src={getTemplateThumb(template)}
                alt=""
              />
            </Tooltip>
          ) : (
            <Tooltip tipText={template.name} direction="left" />
          ))
        );
      },
    },
  ];

  const table = useReactTable({
    data: localCampaigns || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    manualPagination: true, // Enable manual pagination
    state: {
      pagination,
      sorting,
    },
  });

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch Audiences',
      })
    );
  }

  const getStatus = campaignData => {
    const now = new Date();
    const endTime = new Date(campaignData.end_date);
    const hasEnded = campaignData.end_date && endTime.getTime() < now.getTime();
    const status = campaignData.enabled
      ? hasEnded
        ? 'Ended'
        : 'Delivering'
      : 'Inactive';

    return status;
  };

  // Handle update campaign status
  const handleUpdateCampaignStatus = useCallback(
    async (campaignData, enabled) => {
      const payload = {
        campaign: {
          enabled,
        },
      };
      try {
        await updateCampaignById({
          id: campaignData.id,
          data: payload,
        }).unwrap();
        dispatch(
          pushNotificationWithTimeout({
            type: 'success',
            message: 'Campaign status updated successfully',
          })
        );
      } catch (error) {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: 'Failed to update campaign status',
          })
        );
      }
    },
    [updateCampaignById, dispatch] // ✅ dependencies
  );

  // Function to update a specific campaign in local state
  const updateCampaignLocally = useCallback(
    (id, updates) => {
      setLocalCampaigns(prev =>
        prev.map(campaign =>
          campaign.id === id ? { ...campaign, ...updates } : campaign
        )
      );
    },
    [setLocalCampaigns] // ✅ dependencies
  );

  const debouncedUpdate = useMemo(
    () =>
      debounce((campaignData, enabled) => {
        updateCampaignLocally(campaignData.id, { enabled });
        handleUpdateCampaignStatus(campaignData, enabled);
      }, 300),
    [updateCampaignLocally, handleUpdateCampaignStatus]
  );

  // Handle update campaign priority
  const handleUpdateCampaignPriority = async (values, form) => {
    setEditingPriorityRowId(null);
    const priority = values.priority ? parseInt(values.priority) : null;
    const campaignId = values.campaignId;

    const payload = {
      campaign: {
        priority,
      },
    };

    form.setSubmitting(true);

    try {
      await updateCampaignById({ id: campaignId, data: payload }).unwrap();
      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Campaign priority updated successfully',
        })
      );
    } catch (error) {
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Faild to update campaign priority',
        })
      );
    } finally {
      form.setSubmitting(false);
    }
  };

  // Handle rows per clicked
  const handleRowClick = row => {
    setSelectedRowId(prev => (prev === row.id ? null : row.id)); // Toggle selection
    navigate(`/campaigns/${encodeURIComponent(row.original.id)}`);
  };

  // Handle sort clicked
  const handleSortClick = header => {
    if (header.column.getNextSortingOrder() === 'asc') {
      header.column.toggleSorting(false); // Set to 'asc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'asc-nulls-last',
          },
        ],
      });
    } else if (header.column.getNextSortingOrder() === 'desc') {
      header.column.toggleSorting(true); // Set to 'desc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'desc-nulls-last',
          },
        ],
      });
    } else {
      header.column.clearSorting(); // Remove sorting
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [],
      });
    }
  };

  const handleDragEnd = async result => {
    const { source, destination } = result;

    if (!destination || source.index === destination.index) return;

    const reordered = [...localCampaigns];
    const [movedItem] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, movedItem);

    // update local campaigns data to avoid animation issue
    setLocalCampaigns(reordered);

    // change priority indext logic base on sort direction
    const priority = sorting[0]?.desc
      ? reordered.length - destination.index
      : destination.index + 1;

    const payload = {
      campaign: {
        priority,
      },
    };

    // Call API to update priority/order
    try {
      await updateCampaignById({ id: movedItem.id, data: payload }).unwrap();
      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Campaign priority updated successfully',
        })
      );
    } catch (error) {
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Faild to update campaign priority',
        })
      );
    }
  };

  const formatUTCDateTime = date => {
    // extract the UTC values from the date object
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1; // add 1 because getUTCMonth() returns 0-11
    const day = date.getUTCDate();
    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    // determine if it's AM or PM
    const ampm = hours >= 12 ? 'PM' : 'AM';

    // convert hours from 24-hour format to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // handle midnight (0 o'clock)

    // pad the values with zeros if they're single digit
    const formattedDay = day < 10 ? `0${day}` : day;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    // return the formatted string with AM or PM appended
    return `${month}/${formattedDay}/${year
      .toString()
      .substr(-2)} ${hours}:${formattedMinutes} ${ampm} UTC`;
  };

  return (
    <Box className="cmp">
      <Box className="cmp-header">
        <Box className="cmp-header__left">
          {!isReadOnly && (
            <Button
              type="button"
              url="/campaigns/new"
              icon="add"
              text="New Campaign"
              textColor={colors.slate}
              btnStyle="transparent"
            />
          )}
        </Box>

        <Box className="cmp-header__nav"></Box>

        <Box className="cmp-header__right"></Box>
      </Box>

      <Box className="cmp-body">
        <Box height="60px" className="rail rail-rule">
          <Box className="flexGrow">
            <Filter
              value={searchInput}
              placeholder="Search campaigns"
              onChange={e => {
                setSearchInput(e); // Update raw input immediately
                handleDebouncedSearch(e); // Trigger debounced update
              }}
            />
          </Box>
          <Box className="cmp-search__hide-disabled">
            <Checkbox
              label="Hide Disabled"
              checked={
                defaultSearchParameters?.enabled === true ||
                defaultSearchParameters?.enabled === 'true'
              }
              onChange={event => {
                setDefaultSearchParameters({
                  ...defaultSearchParameters,
                  enabled: event.target.checked || undefined,
                });
              }}
            />
          </Box>
        </Box>

        {campaignsData?.global_holdout_pct > 0 && (
          <Box
            bg="blue.50"
            borderWidth="1px"
            borderColor="blue.200"
            borderRadius="md"
            px={4}
            py={3}
            mb={3}
          >
            <Text fontSize="sm" color="blue.700">
              <strong>Global Holdout Active:</strong>{' '}
              {campaignsData.global_holdout_pct}% of all customers are excluded
              from receiving inserts
            </Text>
          </Box>
        )}

        <Box className={!isMobileView ? 'cmp-table' : 'cmp-table-mobile'}>
          {(isLoading || isFetching) && <Progress size="xs" isIndeterminate />}
          {!isMobileView && (
            <Table size="md" variant="simple">
              <Thead className="cmp-table__table-head">
                {table.getHeaderGroups().map(headerGroup => (
                  <Tr className="cmp-table__row" key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      const textAlign =
                        header.column.columnDef.textAlign || 'left';
                      const width = header.column.columnDef.width || null;

                      return (
                        <Th
                          className="cmp-table__header"
                          key={header.id}
                          whiteSpace="nowrap"
                          textAlign={textAlign}
                          width={width}
                        >
                          {header.column.getCanSort() ? (
                            <Flex justify={textAlign} align="center">
                              <Text
                                className="cmp-table__header-text"
                                mr={2}
                                onClick={() => handleSortClick(header)}
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                              </Text>

                              <Box
                                onClick={() => handleSortClick(header)}
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {defaultSearchParameters.sort[0]?.field ===
                                header.id ? (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={
                                      defaultSearchParameters.sort[0]
                                        ?.direction || 'asc'
                                    }
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                ) : (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={'normal'}
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                )}
                              </Box>
                            </Flex>
                          ) : (
                            <Text className="cmp-table__header-text" mr={2}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </Text>
                          )}
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="campaigns">
                  {provided => (
                    <Tbody ref={provided.innerRef} {...provided.droppableProps}>
                      {table.getRowModel().rows.map((row, index) => {
                        const campaignData = row.original;

                        return (
                          <Draggable
                            key={campaignData.id}
                            draggableId={campaignData.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Tr
                                key={row.id}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                // onClick={() => handleRowClick(row)}
                                bg={
                                  snapshot.isDragging
                                    ? 'gray.200' // highlight dragging row
                                    : row.id === selectedRowId
                                    ? 'gray.100'
                                    : 'white'
                                }
                                color={colors.slate}
                                _hover={{
                                  bg: colors.lighterGrey,
                                  textColor: colors.deepSlate2,
                                }}
                                cursor="pointer"
                              >
                                {row.getVisibleCells().map((cell, index) => {
                                  const campaignData = row.original;
                                  const textAlign =
                                    cell.column.columnDef.textAlign || 'left';
                                  const width =
                                    cell.column.columnDef.width || null;
                                  const status = getStatus(campaignData);
                                  let indicatorColor;

                                  if (
                                    status === 'Delivering' ||
                                    campaignData.is_campaign_using ||
                                    campaignData.enabled
                                  ) {
                                    indicatorColor = colors.status00;
                                  }
                                  if (
                                    status === 'Inactive' ||
                                    !campaignData.enabled
                                  ) {
                                    indicatorColor = colors.greyDisabled;
                                  }
                                  if (status === 'Not Delivering') {
                                    indicatorColor = colors.status100;
                                  }
                                  return (
                                    <Td
                                      className="cmp-table__body-cell"
                                      px={index === 1 ? '0px' : '15px'}
                                      py="0px"
                                      key={cell.id}
                                      textAlign={textAlign}
                                      width={width}
                                      onClick={() => {
                                        if (index !== 0 && index !== 2) {
                                          handleRowClick(row);
                                        }
                                      }}
                                    >
                                      {index === 1 ? (
                                        <Box className="cmp-table__second-cell-container">
                                          <Box
                                            className="cmp-table__indicator"
                                            bg={indicatorColor}
                                          ></Box>
                                          <Box className="cmp-table__second-cell">
                                            {flexRender(
                                              cell.column.columnDef.cell,
                                              cell.getContext()
                                            )}
                                          </Box>
                                        </Box>
                                      ) : (
                                        flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext()
                                        )
                                      )}
                                    </Td>
                                  );
                                })}
                              </Tr>
                            )}
                          </Draggable>
                        );
                      })}
                    </Tbody>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
          )}

          {isMobileView && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="campaigns" direction="vertical">
                {provided => (
                  <Grid
                    templateColumns="repeat(1, 1fr)"
                    gap={4}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {table.getRowModel().rows.map((row, index) => {
                      const campaign = row.original;
                      const status = getStatus(campaign);
                      const delivered = (
                        campaign.fulfill_count || 0
                      ).toLocaleString();
                      const personalized_prints =
                        campaign.delivery_limit === null
                          ? `${delivered} / ∞`
                          : `${delivered} / ${campaign.delivery_limit.toLocaleString()}`;

                      const template = templatesData?.data.entities.find(
                        template => template.id === campaign.ad_template_id
                      );
                      const hasTemplateThumb =
                        template && getTemplateThumb(template);

                      return (
                        <Draggable
                          key={campaign.id}
                          draggableId={campaign.id.toString()}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <GridItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              borderWidth="1px"
                              borderRadius="lg"
                              bg="white"
                              boxShadow={snapshot.isDragging ? 'lg' : 'sm'}
                              cursor="pointer"
                            >
                              <Table>
                                <Tbody>
                                  <Tr>
                                    <Td colSpan={2}>
                                      <Flex align="center" gap={3}>
                                        {!isReadOnly && !isMobileDevice && (
                                          <Text
                                            as={'span'}
                                            className="cmp-header__drag-handle"
                                          >
                                            <i className="fa fa-grip-vertical" />
                                          </Text>
                                        )}
                                        <Box
                                          onClick={e => {
                                            e.stopPropagation();
                                          }}
                                        >
                                          <Switch
                                            size="md"
                                            colorScheme="green"
                                            defaultChecked={campaign.enabled}
                                            onChange={event => {
                                              const enabled =
                                                event.target.checked;
                                              debouncedUpdate(
                                                campaign,
                                                enabled
                                              );
                                            }}
                                          />
                                        </Box>
                                        <Text
                                          as={'span'}
                                          className="cmp-header__legend"
                                        >
                                          <i
                                            className="fas fa-circle"
                                            style={{
                                              color:
                                                campaign.target_type === 'open'
                                                  ? colors.primary900
                                                  : colors.secondary900,
                                            }}
                                          />
                                        </Text>
                                        <Text>{campaign.name}</Text>
                                        {campaign.holdout_pct > 0 && (
                                          <Text
                                            as="span"
                                            fontSize="xs"
                                            bg="#FEF3CD"
                                            color="#856404"
                                            px={2}
                                            py={0.5}
                                            borderRadius="full"
                                            whiteSpace="nowrap"
                                            fontWeight="semibold"
                                          >
                                            {campaign.holdout_pct}% Holdout
                                          </Text>
                                        )}
                                      </Flex>
                                    </Td>
                                  </Tr>
                                  <Tr>
                                    <Td
                                      width="50%"
                                      borderRight="1px"
                                      borderColor="#edf2f7"
                                    >
                                      <Text>
                                        <strong>Status:</strong> {status}
                                      </Text>
                                    </Td>
                                    <Td width="50%">
                                      <Text>
                                        <strong>Personalized Prints:</strong>{' '}
                                        {personalized_prints}
                                      </Text>
                                    </Td>
                                  </Tr>
                                  <Tr>
                                    <Td
                                      width="50%"
                                      borderRight="1px"
                                      borderColor="#edf2f7"
                                    >
                                      <Text>
                                        <strong>Start Date:</strong>{' '}
                                        {campaign.start_date
                                          ? formatUTCDateTime(
                                              new Date(campaign.start_date)
                                            )
                                          : '--'}
                                      </Text>
                                    </Td>
                                    <Td width="50%">
                                      <Text>
                                        <strong>End Date:</strong>{' '}
                                        {campaign.end_date
                                          ? formatUTCDateTime(
                                              new Date(campaign.end_date)
                                            )
                                          : '--'}
                                      </Text>
                                    </Td>
                                  </Tr>
                                  <Tr>
                                    <Td
                                      width="50%"
                                      verticalAlign="top"
                                      borderRight="1px"
                                      borderColor="#edf2f7"
                                    >
                                      <Text>
                                        <strong>Priority:</strong>{' '}
                                        {campaign.priority}
                                      </Text>
                                    </Td>
                                    <Td width="50%">
                                      <Text mb={1}>
                                        <strong>Template:</strong>
                                      </Text>
                                      <Flex
                                        alignItems="center"
                                        justifyContent="center"
                                      >
                                        {template &&
                                          (hasTemplateThumb ? (
                                            <Image
                                              className="cmp-table__template-preview"
                                              src={getTemplateThumb(template)}
                                              alt="template image"
                                            />
                                          ) : (
                                            <Image
                                              className="cmp-table__template-preview"
                                              src={placeholderListViewImg}
                                              alt="placeholder image"
                                            />
                                          ))}
                                      </Flex>
                                    </Td>
                                  </Tr>
                                </Tbody>
                              </Table>
                            </GridItem>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Grid>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Box>
        <Pagination
          table={table}
          data={campaignsData}
          pagination={pagination}
          setPagination={setPagination}
          setSelectedRowId={setSelectedRowId}
        />
      </Box>

      <Box className="cmpFooter"></Box>
    </Box>
  );
};

export default Campaigns;
